import { useRef, useState } from "react"
import { Box, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faComment } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"

import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { StandardDialog } from "../../../../../../../shared/components/dialogs/StandardDialog"
import { useInlineClampedText } from "../../../../../../../shared/components/createOrder/components/drawer/messages/useInlineClampedText"

const useStyles = makeStyles()((theme) => ({
    comment: {
        padding: ".75rem 1rem",
        borderRadius: ".25rem",
        background: theme.palette.tailwind.sky[50],
    },
    icon: {
        flexShrink: 0,
        fontSize: "1rem",
        color: theme.palette.tailwind.blue[500],
    },
    textContainer: {
        position: "relative",
        flex: 1,
        minWidth: 0,
        maxWidth: "50rem",
    },
    previewText: {
        display: "-webkit-box",
        WebkitLineClamp: 1,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        overflowWrap: "anywhere",
        color: theme.palette.tailwind.slate[500],
    },
    readMore: {
        fontWeight: 700,
        color: theme.palette.tailwind.blue[500],
        cursor: "pointer",
    },
    dialogNote: {
        padding: "1.5rem",
        color: theme.palette.tailwind.slate[800],
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
    },
    measurerRoot: {
        position: "absolute",
        visibility: "hidden",
        pointerEvents: "none",
        top: "-10000px",
        left: "-10000px",
    },
    measurerText: {
        overflowWrap: "anywhere",
    },
}))

type Props = Readonly<{
    note: string
}>

export function ProductionOrderComment({ note }: Props) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    const [isDialogOpen, setIsDialogOpen] = useState(false)

    const observeSizeRef = useRef<HTMLDivElement | null>(null)
    const measureRef = useRef<HTMLParagraphElement | null>(null)
    const measurePrefixRef = useRef<HTMLSpanElement | null>(null)
    const measureSuffixRef = useRef<HTMLSpanElement | null>(null)

    const readMoreLabel = l10n.getString("meeting-service-production-view-order-comment-read-more")
    const readMoreSuffix = ` ${readMoreLabel}`

    const { previewText, isTruncated } = useInlineClampedText({
        fullText: note,
        maxLines: 1,
        suffixText: readMoreSuffix,
        measureRef,
        measurePrefixRef,
        measureSuffixRef,
        observeSizeRef,
    })

    return (
        <Stack direction="row" alignItems="center" spacing=".5rem" className={classes.comment}>
            <FontAwesomeIcon icon={faComment} className={classes.icon} />
            <Box className={classes.textContainer} ref={observeSizeRef}>
                <Typography variant="body2" component="span" className={classes.previewText}>
                    {isTruncated ? previewText : note}
                    {isTruncated && (
                        <span className={classes.readMore} onClick={() => setIsDialogOpen(true)}>
                            {readMoreSuffix}
                        </span>
                    )}
                </Typography>
            </Box>
            <StandardDialog
                open={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                disableMobileFullScreen
                dialogTitle={l10n.getString("meeting-service-production-view-order-comment-dialog-title")}
            >
                <Typography variant="body1" className={classes.dialogNote}>
                    {note}
                </Typography>
            </StandardDialog>
            <Box className={classes.measurerRoot} aria-hidden>
                <Typography variant="body2" className={classes.measurerText} ref={measureRef}>
                    <span ref={measurePrefixRef} />
                    <span ref={measureSuffixRef} />
                </Typography>
            </Box>
        </Stack>
    )
}
